import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Upload, X, Edit, Trash2, Package, Search, Eye, Video, CheckCircle, XCircle, ShieldCheck, AlertCircle } from "lucide-react";

const categories = [
  "Electronics",
  "Fashion & Clothing",
  "Home & Garden",
  "Beauty & Health",
  "Sports & Outdoors",
  "Toys & Games",
  "Books & Media",
  "Automotive",
  "Food & Beverages",
  "Office Supplies",
  "Jewelry & Accessories",
  "Pet Supplies",
];

interface Product {
  id: string;
  title: string;
  description: string | null;
  price: number;
  category: string | null;
  stock: number | null;
  status: string | null;
  images: string[] | null;
  video_url: string | null;
  views: number | null;
  likes: number | null;
  created_at: string;
  updated_at: string;
  approved?: boolean;
  approved_at?: string;
  approved_by?: string;
  rejection_reason?: string;
  seller_id?: string;
  seller?: {
    business_name?: string;
    full_name?: string;
  };
}

export default function ProductManagement() {
  const { toast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    category: "",
    stock: "",
    status: "active" as "active" | "draft",
  });

  // Fetch all products for admin
  const fetchProducts = async () => {
    const { data, error } = await supabase
      .from("products")
      .select('*')
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        title: "Error fetching products",
        description: error.message,
        variant: "destructive",
      });
    } else {
      // Fetch seller info separately
      if (data) {
        const sellerIds = Array.from(new Set(data.map(p => p.seller_id).filter(Boolean)));
        let sellersMap: Record<string, any> = {};
        
        if (sellerIds.length > 0) {
          const { data: sellers } = await supabase
            .from('profiles')
            .select('id, business_name, full_name')
            .in('id', sellerIds);
          
          if (sellers) {
            sellersMap = sellers.reduce((acc: any, s: any) => {
              acc[s.id] = s;
              return acc;
            }, {});
          }
        }
        
        const productsWithSellers = data.map((product: any) => ({
          ...product,
          seller: sellersMap[product.seller_id] || {}
        }));
        
        setProducts(productsWithSellers);
      }
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages((prev) => [...prev, reader.result as string].slice(0, 5));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a video file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (100MB max)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Video must be less than 100MB",
        variant: "destructive",
      });
      return;
    }

    // Create video element to check duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      
      // Check if video is longer than 1 minute (60 seconds)
      if (video.duration > 60) {
        toast({
          title: "Video Too Long",
          description: "Video must be 1 minute or less",
          variant: "destructive",
        });
        return;
      }

      // Video is valid, set it
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    };

    video.src = URL.createObjectURL(file);
  };

  const removeVideo = () => {
    setVideoFile(null);
    setVideoPreview("");
  };

  const uploadVideo = async (): Promise<string | null> => {
    if (!videoFile) return null;

    const { data: profile } = await supabase.auth.getUser();
    if (!profile.user) return null;

    const fileName = `${profile.user.id}/${Date.now()}-${videoFile.name}`;
    
    const { error: uploadError } = await supabase.storage
      .from('product-videos')
      .upload(fileName, videoFile);

    if (uploadError) {
      toast({
        title: "Video Upload Failed",
        description: uploadError.message,
        variant: "destructive",
      });
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('product-videos')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (images.length === 0 && !editingProduct) {
      toast({
        title: "Image Required",
        description: "Please upload at least one product image",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setUploading(true);
    
    const { data: profile } = await supabase.auth.getUser();
    if (!profile.user) {
      toast({
        title: "Authentication Error",
        description: "Please login to continue",
        variant: "destructive",
      });
      setLoading(false);
      setUploading(false);
      return;
    }

    try {
      // Upload video if present
      let videoUrl = editingProduct?.video_url || null;
      if (videoFile) {
        videoUrl = await uploadVideo();
      }

      // Upload images to storage
      const uploadedImageUrls: string[] = [];
      for (const image of images) {
        // Skip if image is already a URL (from editing)
        if (image.startsWith('http')) {
          uploadedImageUrls.push(image);
          continue;
        }

        // Convert base64 to blob
        const response = await fetch(image);
        const blob = await response.blob();
        const file = new File([blob], `product-${Date.now()}.jpg`, { type: 'image/jpeg' });

        const fileName = `${profile.user.id}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('products')
          .upload(fileName, file);

        if (uploadError) {
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('products')
          .getPublicUrl(fileName);

        uploadedImageUrls.push(publicUrl);
      }

      const productData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        stock: parseInt(formData.stock),
        status: formData.status,
        images: uploadedImageUrls,
        video_url: videoUrl,
      };

      if (editingProduct) {
        // Don't change seller_id when admin updates - preserve original seller
        const { error } = await supabase
          .from("products")
          .update(productData)
          .eq("id", editingProduct.id);

        if (error) {
          toast({
            title: "Error updating product",
            description: error.message,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Product Updated",
            description: "Product has been updated successfully",
          });
          setOpenDialog(false);
          fetchProducts();
        }
      } else {
        // Only set seller_id for new products (admin's own products)
        const { error } = await supabase
          .from("products")
          .insert([{ ...productData, seller_id: profile.user.id }]);

        if (error) {
          toast({
            title: "Error adding product",
            description: error.message,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Product Added",
            description: "Product has been added to the admin store (No commission applied)",
          });
          setOpenDialog(false);
          fetchProducts();
        }
      }

      resetForm();
    } catch (error: any) {
      toast({
        title: "Upload Error",
        description: error.message || "Failed to upload product media",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      price: "",
      category: "",
      stock: "",
      status: "active",
    });
    setImages([]);
    setVideoFile(null);
    setVideoPreview("");
    setEditingProduct(null);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description || "",
      price: product.price.toString(),
      category: product.category || "",
      stock: product.stock?.toString() || "",
      status: product.status as "active" | "draft" || "active",
    });
    setImages(product.images || []);
    setVideoPreview(product.video_url || "");
    setOpenDialog(true);
  };

  const handleDelete = async (productId: string) => {
    if (!confirm("Are you sure you want to delete this product?")) return;

    const { error } = await supabase
      .from("products")
      .delete()
      .eq("id", productId);

    if (error) {
      toast({
        title: "Error deleting product",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Product Deleted",
        description: "Product has been removed from the store",
      });
      fetchProducts();
    }
  };

  const handleApproveProduct = async (productId: string) => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) return;

    const { error } = await supabase.rpc('approve_product', {
      p_product_id: productId,
      p_admin_id: userData.user.id
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to approve product",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Product Approved",
        description: "Product has been approved and is now live",
      });
      fetchProducts();
    }
  };

  const handleRejectProduct = async (productId: string) => {
    const reason = prompt("Please provide a reason for rejection:");
    if (!reason) return;

    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) return;

    const { error } = await supabase.rpc('reject_product', {
      p_product_id: productId,
      p_admin_id: userData.user.id,
      p_reason: reason
    });

    if (error) {
      toast({
        title: "Error", 
        description: error.message || "Failed to reject product",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Product Rejected",
        description: "Product has been rejected and seller notified",
      });
      fetchProducts();
    }
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || product.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected =
    filteredProducts.length > 0 && filteredProducts.every((p) => selectedIds.has(p.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredProducts.forEach((p) => next.delete(p.id));
      } else {
        filteredProducts.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const handleBulkStatusChange = async (status: "active" | "draft") => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const label = status === "active" ? "activate" : "move to draft";
    if (!confirm(`Are you sure you want to ${label} ${ids.length} product(s)?`)) return;

    setBulkUpdating(true);
    const { error } = await supabase
      .from("products")
      .update({ status })
      .in("id", ids);
    setBulkUpdating(false);

    if (error) {
      toast({
        title: "Bulk update failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Products updated",
        description: `${ids.length} product(s) ${status === "active" ? "are now live" : "moved to draft"}.`,
      });
      setSelectedIds(new Set());
      fetchProducts();
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "active":
        return <Badge className="bg-success/10 text-success">Active</Badge>;
      case "draft":
        return <Badge className="bg-muted-foreground/10 text-muted-foreground">Draft</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getStockBadge = (stock: number | null) => {
    if (!stock || stock === 0) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    } else if (stock < 10) {
      return <Badge className="bg-warning/10 text-warning">Low Stock</Badge>;
    }
    return <Badge className="bg-success/10 text-success">In Stock</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{products.length}</div>
            <p className="text-xs text-success mt-1">Unlimited uploads</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {products.filter(p => p.status === "active").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Stock</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {products.reduce((sum, p) => sum + (p.stock || 0), 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Commission Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">0%</div>
            <p className="text-xs text-success mt-1">No commission fees</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full md:w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
        <Dialog open={openDialog} onOpenChange={(open) => {
          if (!open) resetForm();
          setOpenDialog(open);
        }}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProduct ? "Edit Product" : "Add New Product"}
              </DialogTitle>
              <DialogDescription>
                {editingProduct 
                  ? "Update the product details below" 
                  : "Add a new product to the admin store (No commission fees)"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Product Images */}
              <div className="space-y-2">
                <Label>Product Images *</Label>
                <div className="grid grid-cols-5 gap-2">
                  {images.map((image, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={image}
                        alt={`Product ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6"
                        onClick={() => removeImage(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <label className="aspect-square border-2 border-dashed border-muted-foreground/25 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                      <Upload className="h-6 w-6 text-muted-foreground mb-1" />
                      <span className="text-xs text-muted-foreground">Upload</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Product Video */}
              <div className="space-y-2">
                <Label>Product Video (Optional - Max 1 minute)</Label>
                {videoPreview ? (
                  <div className="relative">
                    <video
                      src={videoPreview}
                      controls
                      className="w-full rounded-lg"
                      style={{ maxHeight: "300px" }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={removeVideo}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center w-full p-6 border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:border-primary transition-colors">
                    <div className="text-center">
                      <Video className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Upload Product Video</p>
                      <p className="text-xs text-muted-foreground mt-1">MP4, WebM (Max: 1 minute, 100MB)</p>
                    </div>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Product Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Product Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price and Stock */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price (₦) *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stock">Stock Quantity *</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    min="0"
                    required
                  />
                </div>
              </div>

              {/* Product Status */}
              <div className="space-y-2">
                <Label htmlFor="status">Product Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: "active" | "draft") => 
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setOpenDialog(false);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                      {editingProduct ? "Updating..." : "Adding..."}
                    </>
                  ) : (
                    <>
                      {editingProduct ? "Update Product" : "Add Product"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle>Platform Products Management</CardTitle>
          <CardDescription>
            Review and approve all products listed on the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          {selectedIds.size > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 p-3 rounded-lg border bg-muted/40">
              <span className="text-sm font-medium">
                {selectedIds.size} product(s) selected
              </span>
              <div className="flex gap-2 sm:ml-auto">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={bulkUpdating}
                  onClick={() => handleBulkStatusChange("active")}
                >
                  <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                  Set Active
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={bulkUpdating}
                  onClick={() => handleBulkStatusChange("draft")}
                >
                  <XCircle className="h-4 w-4 mr-1 text-muted-foreground" />
                  Set Draft
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bulkUpdating}
                  onClick={() => setSelectedIds(new Set())}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allFilteredSelected}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all products"
                  />
                </TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Seller</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Approval</TableHead>
                <TableHead>Views</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    No products found. Add your first product to get started.
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product.id} data-state={selectedIds.has(product.id) ? "selected" : undefined}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(product.id)}
                        onCheckedChange={() => toggleSelect(product.id)}
                        aria-label={`Select ${product.title}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {product.images && product.images[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.title}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium">{product.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {product.description?.substring(0, 50)}...
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {product.seller?.business_name || product.seller?.full_name || "Admin"}
                      </div>
                    </TableCell>
                    <TableCell>{product.category || "-"}</TableCell>
                    <TableCell>₦{product.price.toLocaleString()}</TableCell>
                    <TableCell>{getStockBadge(product.stock)}</TableCell>
                    <TableCell>{getStatusBadge(product.status)}</TableCell>
                    <TableCell>
                      {product.approved === true ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Approved
                        </Badge>
                      ) : product.approved === false && product.rejection_reason ? (
                        <Badge className="bg-red-100 text-red-700">
                          <XCircle className="h-3 w-3 mr-1" />
                          Rejected
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-700">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{product.views || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {product.approved !== true && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleApproveProduct(product.id)}
                            title="Approve Product"
                          >
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </Button>
                        )}
                        {product.approved !== false && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRejectProduct(product.id)}
                            title="Reject Product"
                          >
                            <XCircle className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(product)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}